import {createFileRoute} from '@tanstack/react-router'
import {type} from 'arktype'

import {BookingType, TravelClass} from '@/lib/flight/types'
import {isDateValueString, validatePassengers} from '@/lib/flight/utils'
import stripDefaultSearchParams from '@/utils/router/middlewares/stripDefaultSearchParams'
import stripEmptySearchParams from '@/utils/router/middlewares/stripEmptySearchParams'
import stripZeroSearchParams from '@/utils/router/middlewares/stripZeroSearchParams'
import {fallbackToDefault} from '@/utils/schema/fallback'
import searchParamString from '@/utils/schema/searchParamString'
export const FLIGHT_ROUTE_DEFAULT_SEARCH = {
  pax: {
    adult: 1,
  },
  type: BookingType.RoundTrip,
  class: TravelClass.Economy,
  token: '',
  departureDate: '',
  arrivalDate: '',
  departureAirport: '',
  arrivalAirport: '',
}

export const flightSearchSchema = type({
  type: fallbackToDefault(
    type.enumerated(...Object.values(BookingType)),
    FLIGHT_ROUTE_DEFAULT_SEARCH.type,
  ),
  class: fallbackToDefault(
    type.enumerated(...Object.values(TravelClass)),
    FLIGHT_ROUTE_DEFAULT_SEARCH.class,
  ),
  pax: fallbackToDefault(
    type({
      'adult?': 'number.integer >= 0',
      'children?': type('number.integer >= 0'),
      'infantsInSeat?': type('number.integer >= 0'),
      'infantsOnLap?': type('number.integer >= 0'),
    }).narrow((value, ctx) => {
      const result = validatePassengers(
        value.adult ?? 0,
        value.children ?? 0,
        value.infantsInSeat ?? 0,
        value.infantsOnLap ?? 0,
      )

      if (result === true) return true
      return ctx.mustBe('valid')
    }),
    FLIGHT_ROUTE_DEFAULT_SEARCH.pax,
  ),
  departureDate: fallbackToDefault(
    searchParamString.narrow((value, ctx) => {
      try {
        if (isDateValueString(value)) return true
      } catch {
        /* empty */
      }

      return ctx.mustBe('valid date value string')
    }),
    FLIGHT_ROUTE_DEFAULT_SEARCH.departureDate,
  ),
  arrivalDate: fallbackToDefault(
    searchParamString.narrow((value, ctx) => {
      try {
        if (isDateValueString(value)) return true
      } catch {
        /* empty */
      }

      return ctx.mustBe('valid date value string')
    }),
    FLIGHT_ROUTE_DEFAULT_SEARCH.arrivalDate,
  ),
  departureAirport: fallbackToDefault(
    searchParamString,
    FLIGHT_ROUTE_DEFAULT_SEARCH.departureAirport,
  ),
  arrivalAirport: fallbackToDefault(searchParamString, FLIGHT_ROUTE_DEFAULT_SEARCH.arrivalAirport),
  token: fallbackToDefault(type('string'), FLIGHT_ROUTE_DEFAULT_SEARCH.token),
})

export type FlightSearch = typeof flightSearchSchema.inferOut

export const Route = createFileRoute('/_flight/flights/')({
  validateSearch: flightSearchSchema,
  search: {
    middlewares: [
      stripDefaultSearchParams(FLIGHT_ROUTE_DEFAULT_SEARCH),
      stripZeroSearchParams(),
      stripEmptySearchParams(),
    ],
  },
})
